import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiCheckCircle, FiUsers, FiTarget, FiAward, FiZap, FiTrendingUp, FiCode, FiGlobe, FiDroplet, FiActivity, FiBarChart2, FiCpu } from 'react-icons/fi';
import logoCF1 from '../assets/CF2.svg';

const HomePage = () => {
  const [animatedStats, setAnimatedStats] = useState({
    activity: 0,
  });

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setAnimatedStats({
        activity: 3183,
      });
      return;
    }

    const timer = setTimeout(() => {
      const animateValue = (start, end, duration, setter) => {
        let startTimestamp = null;
        const step = (timestamp) => {
          if (!startTimestamp) startTimestamp = timestamp;
          const progress = Math.min((timestamp - startTimestamp) / duration, 1);
          const value = Math.floor(progress * (end - start) + start);
          setter(value);
          if (progress < 1) {
            window.requestAnimationFrame(step);
          }
        };
        window.requestAnimationFrame(step);
      };

      animateValue(0, 3183, 2000, (val) => setAnimatedStats(prev => ({...prev, activity: val})));
    }, 500);

    return () => clearTimeout(timer);
  }, [isMobile]);

  const features = useMemo(() => [
    {
      icon: <FiActivity />,
      title: 'Высокая удельная активность',
      description: '3 183 у.е./мг против 667–1 122 у.е./мг у импортного рекомбинантного коагулянта',
      color: '#3B82F6',
      benefit: 'Превосходство над конкурентами по ключевому показателю'
    },
    {
      icon: <FiTarget />,
      title: 'Дешёвое сырьё',
      description: 'Молочная сыворотка и кукурузный экстракт — отходы производства',
      color: '#10B981',
      benefit: 'Снижение себестоимости производства'
    },
    {
      icon: <FiAward />,
      title: 'Продуцент — GRAS',
      description: 'Дрожжи Kluyveromyces lactis со статусом GRAS (FDA)',
      color: '#8B5CF6',
      benefit: 'Безопасный продуцент для пищевой промышленности'
    },
    {
      icon: <FiTrendingUp />,
      title: 'Импортозамещение',
      description: '> 96 % — зависимость РФ от импортных молокосвёртывающих ферментов',
      color: '#F59E0B',
      benefit: 'Локальное производство в России вместо импортных поставок'
    }
  ], []);

  const stats = useMemo(() => [
    { 
      value: `${animatedStats.activity}`, 
      label: 'у.е./мг удельная активность', 
      icon: <FiActivity />,
      description: 'В 2,8–4,8 раза выше, чем у импортного рекомбинантного коагулянта'
    },
    { 
      value: '1 774 ± 90', 
      label: 'у.е./мл активность культуральной жидкости', 
      icon: <FiTarget />,
      description: 'Цикл сокращён с 7 до 5 суток'
    },
    { 
      value: '> 96%', 
      label: 'импортозависимость РФ', 
      icon: <FiTrendingUp />,
      description: 'По молокосвёртывающим ферментам'
    },
    { 
      value: '66%', 
      label: 'рост удельной продуктивности', 
      icon: <FiDroplet />,
      description: 'После оптимизации процесса культивирования'
    }
  ], [animatedStats]);

  const benefits = useMemo(() => [
    {
      title: 'Для производителей',
      items: [
        'Воспроизводимое культивирование штамма-продуцента',
        'Снижение себестоимости производства',
        'Локальное производство в России вместо импортных поставок'
      ]
    },
    {
      title: 'Технологические преимущества',
      items: [
        'Жидкая форма для удобства дозирования',
        'Отнесён к высококачественному универсальному коагулянту',
        'Превосходит коровий фермент по протеолитической активности'
      ]
    },
    {
      title: 'Экономические выгоды',
      items: [
        'Очистка без хроматографии: только ультрафильтрация и диафильтрация',
        'Дешёвое сырьё: молочная сыворотка и кукурузный экстракт',
        'Апробация в СибНИИ сыроделия ФГБНУ ФАНЦА (Барнаул)'
      ]
    }
  ], []);

  return (
    <div className="home-page">
      {!isMobile && (
        <div className="animated-background">
          <div className="gradient-overlay"></div>
          <div className="floating-shapes">
            {[...Array(6)].map((_, i) => (
              <div 
                key={i} 
                className="floating-shape"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  background: `radial-gradient(circle, #3B82F6 0%, transparent 70%)`
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-badge">
                <span className="badge-icon">🧀</span>
                <span className="badge-text">ОПЫТНЫЙ ОБРАЗЕЦ · ПИЛОТНЫЕ ВАРКИ 2026</span>
              </div>
              
              <h1 className="hero-title">
                <span className="title-line">Сыролёт —</span>
                <span className="title-line gradient-text">российский рекомбинантный</span>
                <span className="title-line">химозин для сыроделия</span>
              </h1>
              
              <p className="hero-description">
                Молокосвёртывающий фермент из Kluyveromyces lactis, разработанный при поддержке 
                программы «Студенческий стартап» для импортозамещения в пищевой промышленности.
              </p>
              
              <div className="hero-buttons">
                <Link to="/product" className="btn btn-primary">
                  Узнать о продукте <FiArrowRight className="btn-icon" />
                </Link>
                <Link to="/roadmap" className="btn btn-secondary">
                  Смотреть прогресс
                </Link>
              </div>
            </div>
            
            <div className="hero-visual">
              <div className="cf1-container">
                <img 
                  src={logoCF1} 
                  alt="Сыролёт" 
                  className="cf1-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-icon">
                  {stat.icon}
                </div>
                <div className="stat-content">
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                  <div className="stat-description">{stat.description}</div>
                </div>
                <div className="stat-glow"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="section-header with-purple-line">
            <h2 className="section-title">Ключевые преимущества</h2>
            <p className="section-description">
              Наш продукт сочетает в себе передовые технологии, экономическую эффективность 
              и импортозамещение ключевого ингредиента для российской сыродельной отрасли.
            </p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="feature-card"
                style={{ animationDelay: isMobile ? '0s' : `${index * 0.1}s` }}
              >
                <div className="feature-icon-wrapper">
                  <div 
                    className="feature-icon-bg" 
                    style={{ backgroundColor: `${feature.color}20` }}
                  ></div>
                  <div 
                    className="feature-icon" 
                    style={{ 
                      backgroundColor: feature.color,
                      borderColor: feature.color
                    }}
                  >
                    {feature.icon}
                  </div>
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                <div className="feature-benefit">
                  <FiCheckCircle className="benefit-icon" />
                  <span>{feature.benefit}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <div className="section-header with-purple-line">
            <h2 className="section-title">Кому подойдет наш продукт</h2>
            <p className="section-description">
              Сыролёт разработан для максимальной эффективности в различных сегментах 
              пищевой промышленности и обеспечивает преимущества на каждом этапе производства.
            </p>
          </div>
          
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-block">
                <div className="block-header">
                  <div className="block-number">0{index + 1}</div>
                  <h3 className="block-title">{benefit.title}</h3>
                </div>
                <ul className="block-items">
                  {benefit.items.map((item, i) => (
                    <li key={i} className="block-item">
                      <FiCheckCircle className="item-icon" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Preview - временно скрыт до добавления таблицы */}
      {/* 
      <section className="comparison-preview">
        ...
      </section>
      */}

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-card">
            <div className="cta-content">
              <h2 className="cta-title">Начните сотрудничество с нами</h2>
              <p className="cta-description">
                Получите подробную техническую документацию и возможность проведения тестовых испытаний. 
                Свяжитесь с нами для обсуждения деталей.
              </p>
              <div className="cta-buttons">
                <Link to="/contacts" className="btn btn-primary">
                  <FiZap /> Связаться с нами
                </Link>
                <Link to="/product" className="btn btn-secondary">
                  <FiActivity /> Технические характеристики
                </Link>
              </div>
            </div>
            <div className="cta-visual">
              <div className="pulse-ring"></div>
              <div className="pulse-ring" style={{ animationDelay: '1s' }}></div>
              <div className="pulse-ring" style={{ animationDelay: '2s' }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Grant Support */}
      <section className="grant-section">
        <div className="container">
          <div className="grant-card">
            <div className="grant-icon">
              <FiAward />
            </div>
            <div className="grant-content">
              <h3>Проект реализован при поддержке</h3>
              <p>
                Программы «Студенческий стартап» мероприятия «Платформа университетского 
                технологического предпринимательства» федерального проекта «Технологии». 
                Мы объединяем научный потенциал и инновационные подходы для создания 
                технологий будущего.
              </p>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        .home-page {
          position: relative;
          overflow-x: hidden;
          background: linear-gradient(180deg, #0F172A 0%, #1E293B 100%);
          color: white;
          min-height: 100vh;
        }

        /* Animated Background */
        .animated-background {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: -1;
        }

        .gradient-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                    radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.1) 0%, transparent 50%);
          animation: gradientShift 10s ease-in-out infinite alternate;
        }

        @keyframes gradientShift {
          0% { background-position: 0% 0%; }
          100% { background-position: 100% 100%; }
        }

        .floating-shapes {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        }

        .floating-shape {
          position: absolute;
          width: 300px;
          height: 300px;
          border-radius: 50%;
          opacity: 0.1;
          filter: blur(60px);
          animation: floatShape 20s infinite linear;
        }

        @keyframes floatShape {
          0% { transform: translateY(0) rotate(0deg); }
          100% { transform: translateY(-1000px) rotate(360deg); }
        }

        /* Hero Section */
        .hero-section {
          padding: 120px 0 80px;
          position: relative;
        }

        .hero-content {
          display: grid;
          gap: 60px;
          align-items: center;
        }

        .hero-badge {
          margin-bottom: 20px;
        }

        .badge-text {
          display: inline-block;
          padding: 8px 16px;
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 500;
          color: #3B82F6;
          backdrop-filter: blur(10px);
        }

        .badge-icon {
          margin-right: 8px;
          font-size: 1.2rem;
        }

        .hero-title {
          font-size: 3.5rem;
          margin-bottom: 20px;
          line-height: 1.1;
        }

        .title-line {
          display: block;
        }

        .gradient-text {
          background: linear-gradient(135deg, #3B82F6, #8B5CF6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-description {
          font-size: 1.125rem;
          color: #CBD5E1;
          max-width: 500px;
          margin-bottom: 40px;
          line-height: 1.6;
        }

        .hero-buttons {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 500;
          transition: all 0.3s ease;
          text-decoration: none;
          cursor: pointer;
        }

        .btn-primary {
          background: #3B82F6;
          color: white;
          border: 1px solid #3B82F6;
        }

        .btn-primary:hover {
          background: #2563EB;
          border-color: #2563EB;
        }

        .btn-secondary {
          background: transparent;
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .btn-secondary:hover {
          border-color: #3B82F6;
          background: rgba(59, 130, 246, 0.1);
        }

        .btn-icon {
          transition: transform 0.3s ease;
        }

        .btn:hover .btn-icon {
          transform: translateX(5px);
        }

        /* CF1 Image */
        .hero-visual {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .cf1-container {
          width: 100%;
          max-width: 500px;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .cf1-image {
          width: 100%;
          height: auto;
          max-height: 500px;
          object-fit: contain;
          filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.3));
        }

        /* Stats Section */
        .stats-section {
          padding: 60px 0;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
          align-items: stretch;
        }

        .stat-card {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(16, 185, 129, 0.3);
          border-radius: 16px;
          padding: 24px;
          display: flex;
          align-items: flex-start;
          gap: 16px;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          height: 100%;
          min-height: 200px;
        }

        .stat-card:hover {
          transform: translateY(-5px);
          border-color: #3B82F6;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
        }

        .stat-glow {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #3B82F6, #8B5CF6);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .stat-card:hover .stat-glow {
          opacity: 1;
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          background: rgba(59, 130, 246, 0.1);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #3B82F6;
          font-size: 1.5rem;
          flex-shrink: 0;
        }

        .stat-content {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: white;
          margin-bottom: 4px;
          line-height: 1.2;
        }

        .stat-label {
          font-size: 0.875rem;
          color: #CBD5E1;
          margin-bottom: 8px;
        }

        .stat-description {
          font-size: 0.75rem;
          color: #94A3B8;
          margin-top: auto;
        }

        /* Features Section */
        .features-section {
          padding: 80px 0;
        }

        .section-header {
          text-align: center;
          max-width: 600px;
          margin: 0 auto 60px;
        }

        .section-header.with-purple-line {
          position: relative;
          padding-bottom: 25px;
          margin-bottom: 50px;
        }

        .section-header.with-purple-line::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 500px;
          height: 4px;
          background: linear-gradient(90deg, #8B5CF6, #C084FC, #8B5CF6);
          border-radius: 4px;
          animation: expandLine 1.2s ease-out;
          box-shadow: 0 0 15px rgba(139, 92, 246, 0.5);
        }

        @keyframes expandLine {
          from {
            width: 0;
            opacity: 0;
          }
          to {
            width: 500px;
            opacity: 1;
          }
        }

        .section-title {
          font-size: 2.5rem;
          margin-bottom: 16px;
          color: white;
        }

        .section-description {
          color: #CBD5E1;
          font-size: 1.125rem;
          line-height: 1.6;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
        }

        .feature-card {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 32px;
          text-align: center;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .feature-card:hover {
          transform: translateY(-10px);
          border-color: #3B82F6;
          box-shadow: 0 20px 40px rgba(59, 130, 246, 0.3);
        }

        .feature-icon-wrapper {
          position: relative;
          width: 80px;
          height: 80px;
          margin: 0 auto 24px;
          flex-shrink: 0;
        }

        .feature-icon-bg {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: 50%;
        }

        .feature-icon {
          position: relative;
          width: 100%;
          height: 100%;
          background: #3B82F6;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          color: white;
          border: 2px solid;
          transition: all 0.3s ease;
        }

        .feature-card:hover .feature-icon {
          transform: scale(1.1);
        }

        .feature-title {
          font-size: 1.25rem;
          margin-bottom: 12px;
          color: white;
          flex-shrink: 0;
        }

        .feature-description {
          color: #CBD5E1;
          font-size: 0.875rem;
          line-height: 1.5;
          margin-bottom: 16px;
          flex: 1;
        }

        .feature-benefit {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 16px;
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid rgba(16, 185, 129, 0.2);
          border-radius: 20px;
          color: #10B981;
          font-size: 0.8rem;
          font-weight: 500;
          line-height: 1.4;
          text-align: left;
          margin-top: auto;
          min-height: 70px;
          width: 100%;
        }

        .feature-benefit span {
          flex: 1;
        }

        .benefit-icon {
          flex-shrink: 0;
          font-size: 1rem;
        }

        /* Benefits Section */
        .benefits-section {
          padding: 80px 0;
          background: rgba(255, 255, 255, 0.02);
        }

        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }

        .benefit-block {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 32px;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .benefit-block:hover {
          transform: translateY(-5px);
          border-color: #3B82F6;
        }

        .block-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
          flex-shrink: 0;
        }

        .block-number {
          font-size: 2.5rem;
          font-weight: 700;
          color: #3B82F6;
          opacity: 0.3;
          line-height: 1;
        }

        .block-title {
          font-size: 1.5rem;
          color: white;
          margin: 0;
        }

        .block-items {
          list-style: none;
          padding: 0;
          margin: 0;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .block-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 16px;
          color: #CBD5E1;
          font-size: 0.875rem;
          line-height: 1.5;
        }

        .block-item:last-child {
          margin-bottom: 0;
        }

        .item-icon {
          color: #10B981;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .block-item span {
          flex: 1;
        }

        /* CTA Section */
        .cta-section {
          padding: 80px 0;
        }

        .cta-card {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
          padding: 60px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .cta-content {
          position: relative;
          z-index: 1;
          max-width: 600px;
          margin: 0 auto;
        }

        .cta-title {
          font-size: 2.5rem;
          margin-bottom: 20px;
        }

        .cta-description {
          color: #CBD5E1;
          font-size: 1.125rem;
          margin-bottom: 32px;
          line-height: 1.6;
        }

        .cta-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .cta-visual {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }

        .pulse-ring {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100%;
          height: 100%;
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 24px;
          animation: pulseRing 3s ease-out infinite;
        }

        @keyframes pulseRing {
          0% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(1.1);
          }
        }

        /* Grant Section */
        .grant-section {
          padding: 60px 0 100px;
        }

        .grant-card {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 40px;
          display: flex;
          align-items: center;
          gap: 30px;
        }

        .grant-icon {
          width: 60px;
          height: 60px;
          background: rgba(59, 130, 246, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #3B82F6;
          font-size: 1.5rem;
          flex-shrink: 0;
        }

        .grant-content h3 {
          font-size: 1.5rem;
          margin-bottom: 12px;
          color: white;
        }

        .grant-content p {
          color: #CBD5E1;
          margin: 0;
          line-height: 1.6;
          font-size: 0.875rem;
        }

        /* Responsive */
        @media (min-width: 768px) {
          .hero-content {
            grid-template-columns: 1fr 1fr;
          }

          .hero-title {
            font-size: 4rem;
          }
        }

        /* Мобильные стили */
        @media (max-width: 767px) {
          .home-page {
            background: #0F172A;
          }

          .animated-background,
          .floating-shapes,
          .gradient-overlay {
            display: none;
          }

          .hero-section {
            padding: 80px 0 40px;
          }

          .hero-title {
            font-size: 2.5rem;
          }

          .hero-buttons {
            flex-direction: column;
            width: 100%;
          }

          .hero-buttons .btn {
            width: 100%;
            justify-content: center;
          }

          .cf1-container {
            max-width: 300px;
            margin: 0 auto;
          }

          .cf1-image {
            max-height: 300px;
          }

          .stats-grid,
          .features-grid,
          .benefits-grid {
            grid-template-columns: 1fr !important;
            gap: 16px;
          }

          .stat-card {
            padding: 16px;
            min-height: auto;
          }

          .stat-icon {
            width: 40px;
            height: 40px;
            font-size: 1.2rem;
          }

          .stat-value {
            font-size: 1.5rem;
          }

          .feature-card {
            padding: 24px;
          }

          .feature-benefit {
            min-height: auto;
            padding: 10px;
          }

          .benefit-block {
            padding: 24px;
          }

          .block-number {
            font-size: 2rem;
          }

          .block-title {
            font-size: 1.25rem;
          }

          .section-header.with-purple-line::after {
            width: 280px;
          }

          .cta-section {
            padding: 40px 0;
          }

          .cta-card {
            padding: 40px 20px;
          }

          .cta-title {
            font-size: 2rem;
          }

          .cta-buttons {
            flex-direction: column;
            align-items: center;
          }

          .cta-buttons .btn {
            width: 100%;
            justify-content: center;
          }

          .pulse-ring {
            animation: none;
          }

          .grant-section {
            padding: 40px 0 60px;
          }

          .grant-card {
            flex-direction: column;
            text-align: center;
            padding: 30px 20px;
            gap: 16px;
          }

          .container {
            padding-left: 16px;
            padding-right: 16px;
          }

          .section-header {
            margin-bottom: 32px;
          }

          .section-title {
            font-size: 1.75rem;
          }
        }

        /* Планшетная версия (768px - 1024px) */
        @media (min-width: 768px) and (max-width: 1024px) {
          .container {
            max-width: 900px;
            padding-left: 24px;
            padding-right: 24px;
          }

          .hero-title {
            font-size: 3rem;
          }

          .cf1-container {
            max-width: 400px;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
          }

          .stat-card {
            min-height: 180px;
            padding: 20px;
          }

          .stat-value {
            font-size: 1.5rem;
          }

          .features-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
          }

          .feature-card {
            padding: 24px;
          }

          .feature-benefit {
            min-height: 80px;
            font-size: 0.75rem;
            padding: 10px;
          }

          .benefits-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
          }

          .benefit-block {
            padding: 24px;
          }

          .block-title {
            font-size: 1.2rem;
          }

          .section-header.with-purple-line::after {
            width: 400px;
          }

          .cta-card {
            padding: 40px;
          }

          .cta-title {
            font-size: 2rem;
          }

          .grant-card {
            padding: 30px;
          }
        }
      `}</style>
    </div>
  );
};

export default HomePage;
